import { Check, Plus } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PoolOption, SortingReasonOption } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { SearchInput } from '../../../components/Input';
import { Kbd } from '../../../components/Kbd';
import { ModuleGate } from '../../../components/ModuleGate';
import { fuzzyFilter } from '../../../lib/fuzzy';
import type { RejectChoice } from '../hooks/use-sorting-session';
import styles from './ReasonPicker.module.css';
import { useModule } from '../../../lib/modules';

export type PickerMode = 'reject' | 'pool' | 'eliminate';

/** What each mode says: S22's eliminate takes a finalist off the comparison and sends nothing (Q6). */
const MODE_WORDS: Readonly<Record<PickerMode, { readonly title: string; readonly subtitle: string; readonly confirm: string; readonly missing: string }>> = {
  reject: { title: 'Reject', subtitle: 'Why — the candidate never sees this', confirm: 'Reject', missing: 'Pick a reason before rejecting.' },
  pool: { title: 'Pool', subtitle: 'Why not this role, and which pools', confirm: 'Pool', missing: 'Pick a reason before pooling.' },
  eliminate: { title: 'Eliminate', subtitle: 'Takes them off the comparison — nothing is sent to them', confirm: 'Eliminate', missing: 'Pick a reason before eliminating.' },
};

interface ReasonPickerProps {
  readonly mode: PickerMode;
  readonly reasons: readonly SortingReasonOption[];
  readonly pools: readonly PoolOption[];
  readonly canPool: boolean;
  readonly contactAttempts: number;
  readonly candidateName: string;
  /** R6 (§L31): "Reject the No's" opens with SCR-HM-NO already picked. */
  readonly initialReasonCode?: string;
  /**
   * ADR 0046 Q2 (d): offer "Also add to CV Bank". The screen passes it where its reject request
   * carries the tick, and only for someone with a CV; the server skips anyone without one.
   */
  readonly canAddToCvBank?: boolean;
  readonly onConfirm: (choice: RejectChoice) => void;
}

/**
 * SCR-01's reason picker (ADMIN-07's list) and pool picker, in one anchored panel.
 *
 * Non-negotiable #2: the confirm button stays disabled until a reason is chosen.
 * A pool-eligible reason offers "also add to a pool?" (§L19); P shows only those
 * reasons and needs a pool (DECISIONS-LOG S15 Q2), which can be new — type a name
 * (Q3). Arrow keys move, Enter confirms, Esc closes (the popover's).
 */
export function ReasonPicker({ mode, reasons, pools, canPool, contactAttempts, candidateName, initialReasonCode, canAddToCvBank = false, onConfirm }: ReasonPickerProps): ReactNode {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [reasonCode, setReasonCode] = useState<string | null>(() => (reasons.some((reason) => reason.code === initialReasonCode) ? (initialReasonCode ?? null) : null));
  const [wantsPool, setWantsPool] = useState(mode === 'pool');
  const [poolIds, setPoolIds] = useState<readonly string[]>([]);
  const [newPools, setNewPools] = useState<readonly string[]>([]);
  const [poolQuery, setPoolQuery] = useState('');
  const [wantsCvBank, setWantsCvBank] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // The panel is hidden for its first frame while it is positioned beside its button,
  // and a hidden input cannot take focus — so focus goes in on the next frame (as the
  // command palette does). Without it the keys would keep going to the CV.
  useEffect(() => {
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  const offered = useMemo(() => (mode === 'pool' ? reasons.filter((reason) => reason.triggersPool) : reasons), [mode, reasons]);
  const shown = useMemo(() => fuzzyFilter(query, offered, (reason) => reason.label).map((result) => result.item), [query, offered]);
  const chosen = offered.find((reason) => reason.code === reasonCode) ?? null;
  // R1 (§L34): with Talent pools switched off, no picker offers a pool, wherever it opens.
  const isPoolsOn = useModule('pools');
  const isPooling = canPool && isPoolsOn && chosen?.triggersPool === true && wantsPool;
  // #13: with the CV Bank switched off the tick is not there at all.
  const isCvBankOn = useModule('cv_bank');
  const offersCvBank = mode === 'reject' && canAddToCvBank && isCvBankOn;
  const poolCount = poolIds.length + newPools.length;
  const problem = chosen === null ? MODE_WORDS[mode].missing : isPooling && poolCount === 0 ? 'Choose at least one pool.' : null;

  function isUsable(reason: SortingReasonOption): boolean {
    return reason.minimumContactAttempts === null || contactAttempts >= reason.minimumContactAttempts;
  }

  function confirm(): void {
    if (problem !== null || chosen === null) return;
    onConfirm({ reasonCode: chosen.code, poolIds: isPooling ? poolIds : [], newPoolNames: isPooling ? newPools : [], addToCvBank: offersCvBank && wantsCvBank });
  }

  function moveSelection(step: 1 | -1): void {
    const usable = shown.filter(isUsable);
    if (usable.length === 0) return;
    const index = usable.findIndex((reason) => reason.code === reasonCode);
    const next = usable[(index + step + usable.length) % usable.length];
    if (next) setReasonCode(next.code);
  }

  /**
   * ↑/↓ and Enter work from the moment the picker opens, wherever focus is — a quick
   * R then ↓ arrives before the filter box has taken focus. Listening on the window
   * (capture) rather than on the panel is what makes that hold.
   */
  const keysRef = useRef<(event: globalThis.KeyboardEvent) => void>(() => undefined);
  useEffect(() => {
    keysRef.current = (event) => {
      const target = event.target;
      const isPoolSearch = target instanceof HTMLInputElement && target.dataset.poolSearch !== undefined;
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isPoolSearch) {
        event.preventDefault();
        moveSelection(event.key === 'ArrowDown' ? 1 : -1);
      }
      if (event.key === 'Enter' && !isPoolSearch && !(target instanceof HTMLButtonElement)) {
        event.preventDefault();
        confirm();
      }
    };
  });
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent): void => keysRef.current(event);
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return (
    <div className={styles.picker}>
      <p className={styles.title}>
        {`${MODE_WORDS[mode].title} ${candidateName}`}
        <span className={styles.subtitle}>{MODE_WORDS[mode].subtitle}</span>
      </p>
      <SearchInput ref={searchRef} value={query} placeholder="Filter reasons" aria-label="Filter reasons" aria-controls={listId} onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} />
      <ul id={listId} className={styles.reasons} role="radiogroup" aria-label="Reason">
        {shown.map((reason) => {
          const usable = isUsable(reason);
          const isChosen = reason.code === reasonCode;
          return (
            <li key={reason.code}>
              <button
                type="button"
                role="radio"
                aria-checked={isChosen}
                disabled={!usable}
                className={isChosen ? styles.reasonChosen : styles.reason}
                onClick={() => setReasonCode(reason.code)}
              >
                <span className={styles.reasonLabel}>{reason.label}</span>
                {reason.triggersPool && mode === 'reject' && (
                  <ModuleGate module="pools">
                    <span className={styles.tag}>Pool</span>
                  </ModuleGate>
                )}
                {!usable && <span className={styles.hint}>Needs {reason.minimumContactAttempts} logged contact attempts</span>}
                {isChosen && <Check size={14} aria-hidden className={styles.tick} />}
              </button>
            </li>
          );
        })}
        {shown.length === 0 && <li className={styles.empty}>No reason matches “{query}”.</li>}
      </ul>

      {offersCvBank && (
        <div className={styles.poolPrompt}>
          <Checkbox checked={wantsCvBank} onChange={(checked) => setWantsCvBank(checked)} label="Also add to CV Bank" />
        </div>
      )}
      {canPool && isPoolsOn && chosen?.triggersPool === true && mode === 'reject' && (
        <div className={styles.poolPrompt}>
          <Checkbox checked={wantsPool} onChange={(checked) => setWantsPool(checked)} label="Also add to a talent pool?" />
        </div>
      )}
      {isPooling && (
        <PoolChooser
          pools={pools}
          query={poolQuery}
          onQuery={setPoolQuery}
          chosen={poolIds}
          created={newPools}
          onToggle={(id) => setPoolIds((current) => (current.includes(id) ? current.filter((other) => other !== id) : [...current, id]))}
          onCreate={(name) => {
            setNewPools((current) => (current.some((other) => other.toLowerCase() === name.toLowerCase()) ? current : [...current, name]));
            setPoolQuery('');
          }}
          onRemoveNew={(name) => setNewPools((current) => current.filter((other) => other !== name))}
        />
      )}

      <div className={styles.footer}>
        {problem !== null && <span className={styles.problem}>{problem}</span>}
        <Button variant={mode === 'reject' ? 'danger' : 'solid'} disabled={problem !== null} onClick={confirm}>
          {isPooling ? 'Reject and pool' : MODE_WORDS[mode].confirm}
          <Kbd>Enter</Kbd>
        </Button>
      </div>
    </div>
  );
}

interface PoolChooserProps {
  readonly pools: readonly PoolOption[];
  readonly query: string;
  readonly onQuery: (value: string) => void;
  readonly chosen: readonly string[];
  readonly created: readonly string[];
  readonly onToggle: (id: string) => void;
  readonly onCreate: (name: string) => void;
  readonly onRemoveNew: (name: string) => void;
}

/** A searchable multi-select, with "create new pool" when the name is not there yet (SCR-01, S15 Q3). */
function PoolChooser({ pools, query, onQuery, chosen, created, onToggle, onCreate, onRemoveNew }: PoolChooserProps): ReactNode {
  const shown = fuzzyFilter(query, pools, (pool) => pool.name).map((result) => result.item);
  const trimmed = query.trim();
  const canCreate = trimmed !== '' && !pools.some((pool) => pool.name.toLowerCase() === trimmed.toLowerCase()) && !created.some((name) => name.toLowerCase() === trimmed.toLowerCase());

  return (
    <div className={styles.pools}>
      <SearchInput
        data-pool-search=""
        value={query}
        placeholder="Find or name a pool"
        aria-label="Find or name a pool"
        onChange={(event) => onQuery(event.target.value)}
        onClear={() => onQuery('')}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !canCreate) return;
          event.preventDefault();
          event.stopPropagation();
          onCreate(trimmed);
        }}
      />
      <ul className={styles.poolList} aria-label="Pools">
        {created.map((name) => (
          <li key={`new-${name}`}>
            <Checkbox checked onChange={() => onRemoveNew(name)} label={`${name} (new)`} />
          </li>
        ))}
        {shown.map((pool) => (
          <li key={pool.id}>
            <Checkbox checked={chosen.includes(pool.id)} onChange={() => onToggle(pool.id)} label={`${pool.name} · ${pool.memberCount}`} />
          </li>
        ))}
        {canCreate && (
          <li>
            <button type="button" className={styles.create} onClick={() => onCreate(trimmed)}>
              <Plus size={14} aria-hidden />
              Create “{trimmed}”
            </button>
          </li>
        )}
        {pools.length === 0 && created.length === 0 && !canCreate && <li className={styles.empty}>No pools yet. Type a name to create one.</li>}
      </ul>
    </div>
  );
}
